/**
 * InfinitSales - Manager Dashboard Service
 *
 * API client do Painel do Gestor (aba Minha Empresa, só admin). Uma única
 * chamada de overview devolve todos os blocos (KPIs + gráficos) já recortados
 * pela janela de período. Backend: GET /api/companies/manager-dashboard/overview.
 */
import api from './api';

const managerDashboardService = {
  /**
   * Overview agregado de gestão.
   * @param {number} period - janela em dias (7, 30 ou 90).
   * @param {string|null} category - filtra distribuição/operadores por categoria.
   * Retorna { period, kpis, distribution, leads_timeseries, campaigns,
   *           operators, categories, recent_transfers, categories_available }.
   */
  /**
   * Acesso do usuario aos paineis (papel + grants): {manager_dash, sales_dash,
   * view_members, role}. Mesma fonte da autorizacao das rotas.
   */
  async getAccess() {
    const res = await api.get('/api/companies/manager-dashboard/access');
    return res.data?.data;
  },

  async getOverview(period = 30, category = null, memberId = null, dateFrom = null, dateTo = null) {
    const params = new URLSearchParams();
    params.set('period', String(period));
    if (category) params.set('category', category);
    // Reunião myCar 03/09: recorte por membro e período personalizado.
    if (memberId) params.set('member_id', memberId);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    const res = await api.get(
      `/api/companies/manager-dashboard/overview?${params.toString()}`,
    );
    return res.data?.data;
  },

  /**
   * Painel COMERCIAL (funil de vendas + captação). Só habilita para contas com
   * config de CRM (ex.: myCar). Backend: GET /api/companies/manager-dashboard/
   * sales-overview. Retorna { enabled, kpis, value_coverage, requires_setup }.
   */
  async getSalesOverview(period = 30, memberId = null, dateFrom = null, dateTo = null) {
    const params = new URLSearchParams();
    params.set('period', String(period));
    if (memberId) params.set('member_id', memberId);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    const res = await api.get(
      `/api/companies/manager-dashboard/sales-overview?${params.toString()}`,
    );
    return res.data?.data;
  },

  /**
   * Drill-down de um indicador do Painel Comercial: os leads que compõem o
   * número, com etapa, valor e responsável (para abrir a conversa a partir do
   * card). Backend aplica a mesma governança do painel.
   * @param {string} metric - chave do indicador (ex.: 'carros_vendidos').
   */
  async getSalesOverviewLeads(metric, period = 30, memberId = null, dateFrom = null, dateTo = null) {
    const params = new URLSearchParams();
    params.set('metric', String(metric));
    params.set('period', String(period));
    if (memberId) params.set('member_id', memberId);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    const res = await api.get(
      `/api/companies/manager-dashboard/sales-overview/leads?${params.toString()}`,
    );
    return res.data?.data;
  },

  /**
   * Config de CRM da conta (qualquer membro): informa se o fechamento de ganho
   * captura valor de venda e em qual campo personalizado. Cacheada por sessão —
   * é consultada ao abrir o popup de ganho no Kanban/chat e não muda em uso.
   */
  _crmConfigPromise: null,
  async getCrmConfig() {
    if (!this._crmConfigPromise) {
      this._crmConfigPromise = api
        .get('/api/companies/manager-dashboard/crm-config')
        .then((res) => res.data?.data || { enabled: false })
        .catch(() => {
          // Falha não pode travar o fluxo de etiqueta; tenta de novo na próxima.
          this._crmConfigPromise = null;
          return { enabled: false };
        });
    }
    return this._crmConfigPromise;
  },

  /**
   * Relatorio dos leads de UMA origem, na janela do painel.
   *
   * NAO reusa o export do funil de proposito: aquele caminha por ETAPA, entao
   * conta duas vezes quem esta em dois funis e ignora quem ainda nao tem
   * etapa. Medido em prod (30 dias): dava 220 linhas para os 216 do painel no
   * MERCADO_LIVRE e 27 para 35 no ORGANICO. Esta rota varre a MESMA populacao
   * do bloco de origem, entao o numero clicado e o numero do arquivo.
   */
  async exportLeadSource({ option, period = 30, format = 'csv', detail = 'basico' }) {
    const params = new URLSearchParams();
    params.set('option', option);
    params.set('period', String(period));
    params.set('format', format);
    params.set('detail', detail);
    const res = await api.get(
      `/api/companies/manager-dashboard/lead-sources/export?${params.toString()}`,
      { responseType: 'blob' },
    );
    const cd = res.headers?.['content-disposition'] || '';
    const m = cd.match(/filename="?([^";]+)"?/i);
    const rows = parseInt(res.headers?.['x-export-rows'], 10);
    return {
      blob: res.data,
      filename: (m && m[1]) || `leads.${format === 'xlsx' ? 'xlsx' : 'csv'}`,
      rows: Number.isNaN(rows) ? null : rows,
    };
  },
};

export default managerDashboardService;
